import { useCallback, useState } from "react";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import Tooltip from "ds/components/Tooltip";
import { Clock, Tick } from "components/icons/generated";
import CardCollapsible from "ds/components/Card/Collapsible";
import { useToggle } from "hooks/useToggle";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";
import useUserGuideProgressMutations from "shared/UserGuides/useUserGuideProgressMutations";
import { UserGuideEnriched } from "shared/UserGuides/types";
import {
  USER_GUIDE_RESTART_EVENT_NAME,
  USER_GUIDE_START_EVENT_NAME,
  UserGuideStartLocation,
} from "shared/UserGuides/analytics";
import useAnalytics from "hooks/useAnalytics";
import useAssistantPanelStore from "components/AssistantPanel/Layout/useStore";
import { AssistantPanelView } from "components/AssistantPanel/Layout/types";
import useAssistantPanelLearnStore from "components/AssistantPanel/Learn/useStore";
import useAssistantPanelLearnActiveGuide from "components/AssistantPanel/Learn/useActiveGuide";
import useOpenLearnGuide from "components/AssistantPanel/Learn/useOpenLearnGuide";

import styles from "./styles.module.css";
import UserGuideGroupStatusIndicator from "./StatusIndicator";
import { showUserGuideGroupSwitchConfirmationModal } from "./SwitchConfirmationModal";

type UserGuideGroupGuideCardProps = {
  guide: UserGuideEnriched;
  groupId: string;
  location: UserGuideStartLocation;
  initiallyCollapsed?: boolean;
};

const UserGuideGroupGuideCard = ({
  guide,
  groupId,
  location,
  initiallyCollapsed = true,
}: UserGuideGroupGuideCardProps) => {
  const { navigationState } = useAssistantPanelLearnStore();
  const { isOpen, view } = useAssistantPanelStore();
  const openLearnGuide = useOpenLearnGuide();
  const [isCollapsed, handleToggle] = useToggle(initiallyCollapsed);
  const { startUserGuide, restartUserGuide } = useUserGuideProgressMutations();
  const { activeGuide } = useAssistantPanelLearnActiveGuide();
  const [isStarting, setIsStarting] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);

  const trackSegmentEvent = useAnalytics();

  const isBlocked = guide.hasUnmetPrerequisites;

  const isGuideActiveInPanel =
    isOpen &&
    view === AssistantPanelView.LEARN &&
    navigationState.level === "guide" &&
    guide.isInProgress;

  const executeStart = useCallback(async () => {
    setIsStarting(true);
    trackSegmentEvent(USER_GUIDE_START_EVENT_NAME, { location });
    try {
      await startUserGuide({ id: guide.id });
      openLearnGuide(groupId);
    } finally {
      setIsStarting(false);
    }
  }, [guide.id, startUserGuide, openLearnGuide, groupId, trackSegmentEvent, location]);

  const handleStartGuide = useCallback(() => {
    if (activeGuide) {
      showUserGuideGroupSwitchConfirmationModal({
        activeGuideTitle: activeGuide.metadata.title,
        onConfirm: executeStart,
      });
    } else {
      executeStart();
    }
  }, [activeGuide, executeStart]);

  const handleContinueGuide = useCallback(() => {
    openLearnGuide(groupId);
  }, [openLearnGuide, groupId]);

  const executeRestart = useCallback(async () => {
    setIsRestarting(true);
    trackSegmentEvent(USER_GUIDE_RESTART_EVENT_NAME, { location });
    try {
      await restartUserGuide({ id: guide.id });
      openLearnGuide(groupId);
    } finally {
      setIsRestarting(false);
    }
  }, [guide.id, restartUserGuide, openLearnGuide, groupId, trackSegmentEvent, location]);

  const handleRestartGuide = useCallback(() => {
    if (activeGuide) {
      showUserGuideGroupSwitchConfirmationModal({
        activeGuideTitle: activeGuide.metadata.title,
        onConfirm: executeRestart,
      });
    } else {
      executeRestart();
    }
  }, [activeGuide, executeRestart]);

  return (
    <CardCollapsible
      title={
        <Box direction="row" justify="between" align="center" grow="1">
          <Box direction="column" gap="small">
            <Typography tag="span" variant="p-t7">
              {guide.metadata.title}
            </Typography>
            <Box direction="row" align="center" gap="small">
              <Icon src={Clock} color="secondary" />
              <Tooltip
                active={guide.isCompleted}
                on={(props) => (
                  <Typography {...props} color="secondary" variant="p-body3" tag="span">
                    {guide.timeToComplete}
                  </Typography>
                )}
              >
                Estimated time to complete: {guide.timeToComplete}
              </Tooltip>
            </Box>
          </Box>
          <UserGuideGroupStatusIndicator
            isCompleted={guide.isCompleted}
            isInProgress={guide.isInProgress}
            currentStep={guide.currentStep}
            totalSteps={guide.steps.length}
          />
        </Box>
      }
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      ariaLevel={3}
      ariaLabel={guide.metadata.title}
    >
      <Typography tag="div" variant="p-body3">
        <MarkdownAssistantPanel>{guide.metadata.description}</MarkdownAssistantPanel>
      </Typography>
      {!!guide.metadata.prerequisites.length && (
        <Box direction="column" gap="small">
          <Typography tag="h4" variant="p-body3" margin="medium 0 0 0">
            To be able to complete the scenario you need to have:
          </Typography>

          <ul className={styles.prerequisitesList}>
            {guide.metadata.prerequisites.map((prerequisite) => (
              <li key={prerequisite} className={styles.prerequisiteItem}>
                <Box direction="row" gap="small">
                  <Icon src={Tick} color="success" noShrink />
                  <Typography tag="div" variant="p-body3">
                    <MarkdownAssistantPanel>{prerequisite}</MarkdownAssistantPanel>
                  </Typography>
                </Box>
              </li>
            ))}
          </ul>
        </Box>
      )}
      {!guide.isCompleted && !isGuideActiveInPanel && (
        <Box margin="medium 0 0 0">
          {guide.isInProgress && (
            <Button variant="secondary" size="small" onPress={handleContinueGuide}>
              Continue
            </Button>
          )}
          {!guide.isInProgress && (
            <Tooltip
              active={guide.hasUnmetPrerequisites}
              on={(props) => (
                <Button
                  {...props}
                  variant="primary"
                  size="small"
                  onPress={handleStartGuide}
                  loading={isStarting}
                  disabled={isBlocked || isStarting}
                >
                  Start
                </Button>
              )}
            >
              Complete the previous guides first
            </Tooltip>
          )}
        </Box>
      )}
      {guide.isCompleted && (
        <Box margin="medium 0 0 0">
          <Button
            variant="secondary"
            size="small"
            onPress={handleRestartGuide}
            loading={isRestarting}
            disabled={isRestarting}
          >
            Restart
          </Button>
        </Box>
      )}
    </CardCollapsible>
  );
};

export default UserGuideGroupGuideCard;
